/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { ClipId, PlaylistClip } from "types/Clip";
import { BlockTypes } from "constants/BlockTypes";
import { findClipsAtTime, getPlaylistDuration } from "./PlaylistUtilities";
import { createLogger, EnhancedLogger } from "utilities/Logger";

/**
 * PlaylistManager
 *
 * Manages the sequencing and timing of audio clips in a playlist. The timeline maintains
 * the current state of playback, including which clips should be playing at any given time
 * and which clip should be considered the "foreground" clip (the main clip driving playback).
 *
 * Key Concepts:
 * - Foreground Clip: A transcription or non-background waveform clip that drives the main
 *   playback timing. Only one foreground clip can be active at a time.
 * - Timeline Time: The current playback position in milliseconds from the start of the playlist.
 * - Clip Transitions: Points where foreground clips change, requiring coordination of audio playback.
 *
 * Example Usage:
 * ```typescript
 * const timeline = new PlaylistManager();
 * timeline.updatePlaylist(myPlaylist);
 * timeline.updateTime(5000); // Move to 5 seconds into playlist
 * const currentClips = timeline.getCurrentClips();
 * ```
 */
export class PlaylistManager {
  private playlist: PlaylistClip[] = [];
  private currentTimeMs: number = 0;
  private duration: number = 0;
  private foregroundClipId?: ClipId;

  constructor(
    private readonly logger: EnhancedLogger = createLogger("PlaylistManager")
  ) {}

  updatePlaylist(playlist: PlaylistClip[]): void {
    this.playlist = playlist;
    this.duration = getPlaylistDuration(playlist);

    // When playlist changes, we need to re-evaluate the foreground clip
    this.updateForegroundClip();
  }

  updateTime(newTime: number): void {
    this.currentTimeMs = newTime;
    this.updateForegroundClip();
  }

  progressToNextForegroundClip(clipId: ClipId): void {
    const currentForegroundClip = this.getForegroundClip();

    if (currentForegroundClip?.id !== clipId) {
      return;
    }

    const newForegroundClip = this.playlist.find(
      (clip) =>
        (clip.type === BlockTypes.TRANSCRIPTION ||
          (clip.type === BlockTypes.WAVEFORM && !clip.isBackground)) &&
        clip.startTime > currentForegroundClip?.startTime
    );

    if (newForegroundClip) {
      this.foregroundClipId = newForegroundClip.id;
    }
  }

  private updateForegroundClip(): void {
    // Find potential new foreground clip
    const newForegroundClip = this.getCurrentClips().find(
      (clip) =>
        clip.type === BlockTypes.TRANSCRIPTION ||
        (clip.type === BlockTypes.WAVEFORM && !clip.isBackground)
    );

    // Only update if we need to change the foreground clip
    if (this.shouldUpdateForegroundClip(newForegroundClip)) {
      this.logger.log(
        `Updating foreground clip from ${this.foregroundClipId} to ${newForegroundClip?.id}`
      );
      this.foregroundClipId = newForegroundClip?.id;
    }
  }

  private shouldUpdateForegroundClip(newClip?: PlaylistClip): boolean {
    // If we have no current foreground clip, always update
    if (!this.foregroundClipId) {
      return true;
    }

    const currentForegroundClip = this.getForegroundClip();

    // If current foreground clip doesn't exist in playlist anymore
    if (!currentForegroundClip) {
      return true;
    }

    // If we've passed the end of the current foreground clip
    if (this.currentTimeMs >= currentForegroundClip.endTime) {
      return true;
    }

    // If there's no new clip but we have an existing one that's still valid
    if (!newClip && this.currentTimeMs >= currentForegroundClip.startTime) {
      return false;
    }

    // If the clips are different and the new one should be active
    return (
      newClip?.id !== this.foregroundClipId &&
      this.currentTimeMs >= (newClip?.startTime ?? Infinity)
    );
  }

  getCurrentClips(): PlaylistClip[] {
    return findClipsAtTime(this.playlist, this.currentTimeMs);
  }

  getForegroundClip(): PlaylistClip | undefined {
    if (!this.foregroundClipId) return undefined;
    return this.playlist.find((clip) => clip.id === this.foregroundClipId);
  }

  // Helper to check if we need to transition to next clip soon
  getTimeToNextClipTransition(): number | undefined {
    const current = this.getForegroundClip();
    if (!current) return undefined;

    const timeToEnd = current.endTime - this.currentTimeMs;
    if (timeToEnd < 100 && timeToEnd > 0) {
      return timeToEnd;
    }
  }

  getClip(clipId: ClipId): PlaylistClip | undefined {
    return this.playlist.find((clip) => clip.id === clipId);
  }

  getClipsWithAsset(assetId: string): PlaylistClip[] {
    return this.playlist.filter((clip) => clip.assetId === assetId);
  }

  getCurrentTimeMs(): number {
    return this.currentTimeMs;
  }

  getDurationMs(): number {
    return this.duration;
  }

  getClipsInRange(startTime: number, endTime: number): PlaylistClip[] {
    return this.playlist.filter(
      (clip) => clip.startTime < endTime && clip.endTime > startTime
    );
  }

  canHotSwapToNewPlaylist(
    newPlaylist: PlaylistClip[],
    compare: (oldClip: PlaylistClip, newClip: PlaylistClip) => boolean
  ): boolean {
    if (newPlaylist.length !== this.playlist.length) {
      return false;
    }

    return newPlaylist.every((newClip, idx): boolean => {
      const oldClip = this.playlist[idx];

      return compare(oldClip, newClip);
    });
  }
}
